import {
  ChangeDetectionStrategy,
  Component, ElementRef,
  EventEmitter, HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import {GenericService} from "../../services/generic.service";
import { Observable } from "rxjs";

@Component({
    selector: 'app-weave-pagination',
    templateUrl: './weave-pagination.component.html',
    styleUrls: ['./weave-pagination.component.scss'],
    standalone: false
})
export class WeavePaginationComponent implements OnInit, OnChanges {

  public static DEFAULT_PAGINATOR: paginator = new class implements paginator {
    currentPage: number = 1;
    endIndex: number = 10;
    endPage: number;
    pageSize: number = 10;
    pages: number[];
    startIndex: number = 0;
    startPage: number = 1;
    totalItems: number = 0;
    totalPages: number;
  };

  @ViewChild('paginationDetails') paginationDetails: ElementRef;
  @ViewChild('paginatorSize') paginatorSize: ElementRef;
  @HostListener('document:click', ['$event'])
  clickout(event){
    if(this.paginatorSize){
      if(!this.paginatorSize.nativeElement.contains(event.target)){
        this.pageSizeOpen = false;
      }
      else{
        this.pageSizeOpen = !this.pageSizeOpen;

        if(this.pageSizeOpen) {
          this.findOptionsDropdownPosition();
        }
      }
    }
  }

  @Input() isClientSidePagination = false;
  @Input() pager: paginator = new class implements paginator {
    currentPage: number = 1;
    endIndex: number = 10;
    endPage: number = 1;
    pageSize: number = 10;
    pages: number[] = [1];
    startIndex: number = 0;
    startPage: number = 1;
    totalItems: number = 0;
    totalPages: number = 1;
  };
  @Output() updatedPagination: EventEmitter<paginator> = new EventEmitter<paginator>();
  @Output() pageChanged: EventEmitter<paginator> = new EventEmitter<paginator>();
  @Output() pageSizeChanged: EventEmitter<paginator> = new EventEmitter<paginator>();

  pageSizeOpen: boolean = false;

  // By default show the dropdown options list behind the dropdown
  // In the case that it does not fit in the viewport show it above
  pageSizePositionAboveDropdown: boolean = false;

  pageSizes: {checked: boolean, label: string, value: number}[] = [
    {checked: false, label: "5", value: 5},
    {checked: true, label: "10", value: 10},
    {checked: false, label: "20", value: 20},
    {checked: false, label: "50", value: 50},
    {checked: false, label: "100", value: 100},
  ];
  constructor(private genericService: GenericService) {
  }

  ngOnInit(): void {
    this.paginate(this.pager.totalItems, this.pager.currentPage, this.pager.pageSize);
  }

  ngOnChanges(changes: SimpleChanges) {
    // On every change update the paginator only in the case of the client side pagination
    if(this.isClientSidePagination){
      this.paginate(this.pager.totalItems, this.pager.currentPage, this.pager.pageSize);
    }
  }

  changePage(pageNumber:number, $event = null) {
    if($event) {
      $event.preventDefault();
    }
    this.pager.currentPage = pageNumber;
    this.pager.startIndex = (this.pager.currentPage - 1) * this.pager.pageSize;
    this.pager.endIndex = (this.pager.currentPage) * this.pager.pageSize;
    this.paginate(this.pager.totalItems, this.pager.currentPage, this.pager.pageSize);
    this.pageChanged.emit(this.pager);
  }
  togglePageSizeOpen() {
    this.pageSizeOpen = !this.pageSizeOpen;
  }
  changePageSize(pageSize: {checked: boolean, label: string, value: number}){
    this.pageSizes.forEach(item => {
      if(item.value != pageSize.value ) {
        item.checked = false;
      } else {
        item.checked = true;
      }
    });
    this.pager.pageSize = pageSize.value;
    this.paginate(this.pager.totalItems, this.pager.currentPage, this.pager.pageSize);
    this.pageSizeOpen = false;
    this.pageSizeChanged.emit(this.pager);
  }
  paginate(
    totalItems: number,
    currentPage: number = 1,
    pageSize: number = 10,
    maxPages: number = 10
  ) {
    // calculate total pages
    let totalPages = Math.ceil(totalItems / pageSize);

    // ensure current page isn't out of range
    if (currentPage < 1) {
      currentPage = 1;
    } else if (currentPage > totalPages) {
      currentPage = totalPages;
    }


    let startPage: number, endPage: number;
    if (totalPages <= maxPages) {
      // total pages less than max so show all pages
      startPage = 1;
      endPage = totalPages;
    } else {
      // total pages more than max so calculate start and end pages
      let maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
      let maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
      if (currentPage <= maxPagesBeforeCurrentPage) {
        // current page near the start
        startPage = 1;
        endPage = maxPages;
      } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
        // current page near the end
        startPage = totalPages - maxPages + 1;
        endPage = totalPages;
      } else {
        // current page somewhere in the middle
        startPage = currentPage - maxPagesBeforeCurrentPage;
        endPage = currentPage + maxPagesAfterCurrentPage;
      }
    }

    // calculate start and end item indexes
    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(startIndex + pageSize, totalItems);

    // create an array of pages to ng-repeat in the pager control
    let pages = Array.from(Array((endPage + 1) - startPage).keys()).map(i => startPage + i);

    // return object with all pager properties required by the view
    this.pager = {
      totalItems: totalItems,
      currentPage: currentPage,
      pageSize: pageSize,
      totalPages: totalPages,
      startPage: startPage,
      endPage: endPage,
      startIndex: startIndex,
      endIndex: endIndex,
      pages: pages
    };
    this.updatedPagination.emit(this.pager);
  }

  findOptionsDropdownPosition(){
    // By default show the dropdown options list behind the dropdown
    // In the case that it does not fit in the viewport show it above
    const viewPortHeight = window.innerHeight;
    const dropdownBottomPositionInViewPort = this.paginationDetails.nativeElement.getBoundingClientRect().bottom;

    // Needs at least 600px available space to show the options list behind the dropdown
    this.pageSizePositionAboveDropdown = (viewPortHeight - dropdownBottomPositionInViewPort) < 200;
  }

  resetPager(pager: paginator) {
    this.pager.currentPage = pager.currentPage;
    this.pager.totalItems = pager.totalItems;
    this.paginate(this.pager.totalItems, this.pager.currentPage, this.pager.pageSize);
  }
}

export interface paginator{
  totalItems: number,
  currentPage: number,
  pageSize: number,
  totalPages: number,
  startPage: number,
  endPage: number,
  startIndex: number,
  endIndex: number,
  pages: number[]
}
